import type { ReactNode } from 'react';
import type { KpiItem, OpsBriefBlock } from '../../types/investigator';
import CollapsibleSection from './CollapsibleSection';
import OpsBriefPanel from './OpsBriefPanel';

interface MoreDetailProps {
  /** The answer's ops brief, when it has one. */
  brief?: OpsBriefBlock;
  /** Show the brief's summary strip here: false when it is already the answer's own line. */
  showSummary: boolean;
  /** Every key figure behind the answer. */
  figures: KpiItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What else is inside, named in the closed heading: e.g. "timeline (12)". */
  alsoInside: string[];
  children?: ReactNode;
}

function FigureList({ items }: { items: KpiItem[] }) {
  return (
    <div className="bg-gray-900/30 border border-gray-700/40 rounded-lg p-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Figures</div>
      <dl className="space-y-1 text-sm">
        {items.map((item, i) => (
          <div key={`${item.label}-${i}`} className="leading-snug">
            <dt className="inline text-gray-400">{item.label}: </dt>
            <dd className="inline text-gray-200">
              {item.value}
              {item.unit ? ` ${item.unit}` : ''}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

const lowerFirst = (s: string) => (/^[A-Z][a-z]/.test(s) ? `${s.charAt(0).toLowerCase()}${s.slice(1)}` : s);

/**
 * Whether a summary strip only restates figures listed below it: most strips
 * are the first figures' "label value" joined by " · " (local test, 21 Oct:
 * the same three lines twice). One that adds something (a vehicle, a route)
 * is kept.
 */
function onlyRepeatsFigures(summary: string, figures: KpiItem[]): boolean {
  if (figures.length === 0) return false;
  return summary.split(' · ').every((part) => figures.some((f) => part.trim().startsWith(`${f.label} `)));
}

/**
 * T115 — everything that does not answer the question, behind one closed
 * section under every answer: the summary strip, consignments not asked about
 * (done late, after the ETA), sections marked as detail, every figure, the
 * timeline and the evidence. The heading says what is inside, so nothing is
 * hidden without a trace.
 */
export default function MoreDetail({ brief, showSummary, figures: allFigures, open, onOpenChange, alsoInside, children }: MoreDetailProps) {
  const groups = brief?.answer_first?.more_groups ?? [];
  const sections = brief?.sections.filter((s) => s.detail) ?? [];
  // Figures already shown above, in a section that answers the question, are
  // not repeated here (local test, 21 Oct: revenue and the data overview listed
  // the same lines twice).
  const shownAbove = new Set(
    (brief?.sections ?? [])
      .filter((s) => !s.detail)
      .flatMap((s) => s.rows.flatMap((r) => (r.figure_label ? [r.label, r.figure_label] : [r.label]))),
  );
  const figures = allFigures.filter((f) => !shownAbove.has(f.label));
  // Against every figure, not only those left after removing the ones shown
  // above: a strip of figures shown above is a repeat too (local test, 21 Oct:
  // revenue's strip came back once its two figures had been dropped).
  const hasSummary = showSummary && Boolean(brief?.summary_line) && !onlyRepeatsFigures(brief?.summary_line ?? '', allFigures);
  const inside = [
    ...groups.map((g) => `${g.count} ${lowerFirst(g.title)}`),
    ...(figures.length > 0 || sections.length > 0 || hasSummary ? ['all figures'] : []),
    ...alsoInside,
  ];
  if (inside.length === 0) return null;

  return (
    <CollapsibleSection title="More detail" subtitle={inside.join(' · ')} open={open} onOpenChange={onOpenChange}>
      <div className="space-y-3">
        {brief ? <OpsBriefPanel block={brief} part="detail" hideSummarySection={!hasSummary} /> : null}
        {figures.length > 0 ? <FigureList items={figures} /> : null}
        {children}
      </div>
    </CollapsibleSection>
  );
}
